/**
 * @module-federation/runtime plugin
 * This plugin intercepts remote entry requests (via the `beforeRequest` hook).
 * It looks for remote entry URLs defined in the configuration that follow the pattern:
 *   `baseUrl[javascriptExpression]`
 * (e.g., `https://example.com/remoteEntry.js?v=[new Date().getTime()]`)
 *
 * If a match is found, it evaluates the `javascriptExpression` within the square brackets
 * in the browser's runtime context using `new Function()`.
 * The result of the expression is then appended to the `baseUrl` to form the final URL
 * used to fetch the remote entry script.
 *
 * This allows for dynamic remote URLs based on runtime values, similar to the functionality
 * provided by Webpack's `ExternalTemplateRemotesPlugin` but implemented as a MF runtime plugin.
 */

// Regex to find and extract the base URL and the JS expression within square brackets
const EXPRESSION_REGEX = /^(.*?)(\[[^\]]+\])$/

const evaluateExpressionPlugin = () => ({
  name: 'evaluate-expression-plugin',
  async beforeRequest(args) {
    const { options } = args

    args.options.remotes = options.remotes.map(remote => {
      if (typeof remote.entry === 'string') {
        const match = remote.entry.match(EXPRESSION_REGEX)

        // Check if the entry string matches the pattern "baseUrl[expression]"
        // The regex captures the base URL in group 1 and the [expression] part in group 2
        if (match && match.length === 3) {
          const remoteIdentifier = remote.name || remote.alias
          // Part before [expression]
          const baseUrl = match[1]
          // Extract the expression itself by removing the outer brackets []
          const expression = match[2].slice(1, -1)

          try {
            // Evaluate the extracted expression in the browser context
            const evaluatedValue = new Function(`return ${expression}`)()

            // Construct the new entry URL
            const newEntry = baseUrl + String(evaluatedValue)

            // Return a new remote object with the evaluated entry URL
            return { ...remote, entry: newEntry }
          } catch (error) {
            console.error(
              `EvaluateExpressionPlugin: Failed to evaluate expression for remote '${remoteIdentifier}'. Expression: ${expression}`,
              error,
            )
            // Return remote unmodified if evaluation fails
            return remote
          }
        }
      }
      // Return remote unmodified if it's not a string or doesn't match the pattern
      return remote
    })

    return args
  },
})

export default evaluateExpressionPlugin
